import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Button } from '../components/ui/Button';
import { ContactModal } from '../components/ui/ContactModal';
import { ServicesCarousel } from '../components/ui/ServicesCarousel';
import { useLanguage } from '../context/LanguageContext';
import { Clock, ShieldCheck, HeartPulse, Stethoscope, ClipboardCheck, Activity, CheckCircle, ArrowRight } from 'lucide-react';
export function ServicesPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    t
  } = useLanguage();
  const enhanced = t.services.enhanced;
  return <div className="pt-20" data-id="element-594">
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} data-id="element-595" />

      {/* Hero */}
      <div className="bg-caces-blue text-white py-20" data-id="element-596">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-597">
          <h1 className="font-serif text-4xl md:text-5xl font-bold mb-4" data-id="element-598">
            {t.services.hero.title}
          </h1>
          <p className="text-xl text-caces-light max-w-2xl mx-auto" data-id="element-599">
            {t.services.hero.subtitle}
          </p>
        </div>
      </div>

      {/* Services Carousel */}
      <section className="py-16 bg-white" data-id="element-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-601">
          <SectionHeading title={t.services.hero.title} subtitle={t.services.hero.subtitle} data-id="element-602" />
          <div className="mt-12" data-id="element-603">
            <ServicesCarousel data-id="element-604" />
          </div>
        </div>
      </section>

      {/* 1. Service Highlights */}
      <section className="py-16 bg-caces-sky" data-id="element-605">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-606">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-607">
            {[{
            icon: Clock,
            title: enhanced.highlights.items[0].title
          }, {
            icon: ShieldCheck,
            title: enhanced.highlights.items[1].title
          }, {
            icon: HeartPulse,
            title: enhanced.highlights.items[2].title
          }, {
            icon: Stethoscope,
            title: enhanced.highlights.items[3].title
          }].map((item, i) => <div key={i} className="flex flex-col items-center text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100" data-id="element-608">
                <div className="w-12 h-12 bg-caces-navy/5 rounded-full flex items-center justify-center mb-4 text-caces-navy" data-id="element-609">
                  <item.icon size={24} data-id="element-610" />
                </div>
                <h3 className="font-bold text-caces-navy" data-id="element-611">{item.title}</h3>
              </div>)}
          </div>
        </div>
      </section>

      {/* 2. Surgical Process Overview */}
      <section className="py-20 bg-white" data-id="element-612">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-613">
          <SectionHeading title={enhanced.process.title} subtitle={enhanced.process.subtitle} data-id="element-614" />

          <div className="relative mt-16" data-id="element-615">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-gray-200 -z-10" data-id="element-616"></div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-617">
              {[{
              icon: ClipboardCheck,
              title: enhanced.process.steps[0].title,
              desc: enhanced.process.steps[0].desc
            }, {
              icon: Activity,
              title: enhanced.process.steps[1].title,
              desc: enhanced.process.steps[1].desc
            }, {
              icon: HeartPulse,
              title: enhanced.process.steps[2].title,
              desc: enhanced.process.steps[2].desc
            }].map((step, i) => <div key={i} className="flex flex-col items-center text-center" data-id="element-618">
                  <div className="w-24 h-24 bg-white border-4 border-caces-sky rounded-full flex items-center justify-center mb-6 shadow-sm z-10" data-id="element-619">
                    <step.icon size={32} className="text-caces-accent" data-id="element-620" />
                  </div>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-caces-navy text-white font-bold text-sm mb-4" data-id="element-621">
                    {i + 1}
                  </div>
                  <h3 className="font-serif text-xl font-bold text-caces-navy mb-3" data-id="element-622">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-623">{step.desc}</p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* 3. Support Services Checklist */}
      <section className="py-20 bg-gray-50" data-id="element-624">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-625">
          <div className="max-w-4xl mx-auto" data-id="element-626">
            <SectionHeading title={enhanced.support.title} subtitle={enhanced.support.subtitle} data-id="element-627" />
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm border border-gray-100" data-id="element-628">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6" data-id="element-629">
                {enhanced.support.items.map((item, i) => <div key={i} className="flex items-center gap-4" data-id="element-630">
                    <CheckCircle size={20} className="text-caces-accent flex-shrink-0" data-id="element-631" />
                    <span className="text-gray-700 font-medium" data-id="element-632">{item}</span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Facilities Snapshot */}
      <section className="py-20 bg-white" data-id="element-633">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-634">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-635">
            <div className="relative" data-id="element-636">
              <div className="absolute -inset-4 bg-caces-sky rounded-3xl transform -rotate-2" data-id="element-637"></div>
              <img src="https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1200&q=80" alt="CACES Facilities" className="relative rounded-2xl shadow-lg w-full object-cover aspect-[4/3]" data-id="element-638" />
            </div>
            <div data-id="element-639">
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-caces-navy mb-12" data-id="element-640">
                {enhanced.facilities.title}
              </h2>
              <div className="space-y-10" data-id="element-641">
                {enhanced.facilities.stats.map((stat, i) => <div key={i} className="flex items-center gap-6" data-id="element-642">
                    <div className="w-16 h-16 bg-caces-navy rounded-xl flex items-center justify-center text-white font-bold text-2xl flex-shrink-0" data-id="element-643">
                      {stat.value}
                    </div>
                    <div data-id="element-644">
                      <h3 className="text-xl font-bold text-gray-900" data-id="element-645">
                        {stat.label}
                      </h3>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Soft Institutional Navigation */}
      <section className="py-20 bg-caces-navy text-white" data-id="element-646">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-647">
          <h2 className="font-serif text-3xl font-bold mb-6" data-id="element-648">
            {enhanced.nav.title}
          </h2>
          <p className="text-lg text-caces-light mb-10 max-w-2xl mx-auto" data-id="element-649">
            {enhanced.nav.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-650">
            <Link to="/about" data-id="element-651">
              <Button variant="white" size="lg" data-id="element-652">
                {enhanced.nav.btnAbout}
              </Button>
            </Link>
            <Link to="/about" data-id="element-653">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10 hover:text-white" data-id="element-654">
                {enhanced.nav.btnFacilities}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-50 border-t border-gray-200" data-id="element-655">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-656">
          <h2 className="font-serif text-3xl font-bold text-caces-navy mb-6" data-id="element-657">
            {t.services.cta.title}
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-658">
            {t.services.cta.desc}
          </p>
          <Button size="lg" onClick={() => setIsModalOpen(true)} data-id="element-659">
            {t.services.cta.btn}
          </Button>
        </div>
      </section>
    </div>;
}